import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Leaf, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[calc(100vh-80px)] bg-brand-cream flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-md bg-brand-green flex items-center justify-center mx-auto mb-4">
            <Leaf className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display font-bold text-2xl text-brand-charcoal">Aad Labs Admin</h1>
          <p className="text-sm text-brand-charcoal/60 mt-1">Sign in to view B2B inquiries</p>
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-white rounded-lg p-8 border border-neutral-200 shadow-md space-y-5"
        >
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Email</label>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-neutral-300 rounded-md text-brand-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                placeholder="admin@aadlabs.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Password</label>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-neutral-300 rounded-md text-brand-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-green w-full justify-center disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-center text-xs text-brand-charcoal/50">
          Authorized access only. All activity is logged.
        </div>
      </div>
    </div>
  );
}
